import { FC, KeyboardEvent, MouseEvent } from 'react';
import { Box, IconButton, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useNavigate } from 'react-router-dom';

import styles from './FullAccessBanner.module.scss';
import {
  getPremiumFeatureModalTitle,
  getPremiumFeatureUnavailableMessage,
  getPremiumUpgradeMessage,
  PremiumFeature,
} from '../premiumFeatures';

export const PROFILE_SUBSCRIPTION_URL = '/profile?tab=subscription';

type FullAccessBannerVariant = 'overlay' | 'inline' | 'compact' | 'panel';

interface FullAccessBannerProps {
  variant?: FullAccessBannerVariant;
  className?: string;
  feature?: PremiumFeature;
  onGetFullAccess?: () => void;
  onClose?: () => void;
}

const FullAccessBanner: FC<FullAccessBannerProps> = ({
  variant = 'inline',
  className,
  feature,
  onGetFullAccess,
  onClose,
}) => {
  const navigate = useNavigate();

  const handleGetFullAccess = (event: MouseEvent) => {
    event.stopPropagation();
    if (onGetFullAccess) {
      onGetFullAccess();
      return;
    }
    navigate(PROFILE_SUBSCRIPTION_URL);
  };

  const handleButtonKeyDown = (event: KeyboardEvent) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handleGetFullAccess(event as unknown as MouseEvent);
    }
  };

  const handleClose = (event: MouseEvent) => {
    event.stopPropagation();
    onClose?.();
  };

  const title = feature ? getPremiumFeatureModalTitle(feature) : 'Would you like more options?';
  const unavailableMessage = feature ? getPremiumFeatureUnavailableMessage(feature) : null;
  const upgradeMessage = feature ? getPremiumUpgradeMessage() : 'To get full access, upgrade your plan';

  return (
    <Box
      className={`${styles.fullAccessLabel} ${styles[variant]} ${onClose ? styles.withClose : ''} ${className ?? ''}`}
    >
      {onClose && (
        <IconButton
          className={styles.closeButton}
          onClick={handleClose}
          aria-label="Close"
          size="small"
        >
          <CloseIcon sx={{ width: 24, height: 24 }} />
        </IconButton>
      )}
      <Box className={styles.fullAccessTextContent}>
        <Typography
          variant="h6Bold"
          className={styles.fullAccessTitle}
          sx={{ color: '#222B3A' }}
        >
          {title}
        </Typography>
        {unavailableMessage ? (
          <>
            <Typography
              variant="h9"
              className={styles.fullAccessDescription}
            >
              {unavailableMessage}
            </Typography>
            <Typography
              variant="h9"
              className={styles.fullAccessDescription}
            >
              {upgradeMessage}
            </Typography>
          </>
        ) : (
          <Typography
            variant="h9"
            className={styles.fullAccessDescription}
          >
            {upgradeMessage}
          </Typography>
        )}
      </Box>
      <Typography
        variant="h10Bold"
        component="button"
        type="button"
        className={styles.fullAccessBtn}
        onClick={handleGetFullAccess}
        onKeyDown={handleButtonKeyDown}
      >
        Get full access
      </Typography>
    </Box>
  );
};

export default FullAccessBanner;
